import { useEffect, useMemo } from 'react'
import currency from 'currency.js'

import { StripeProduct } from '@/actions/settings/billingActions'
import { StripeInterval } from '@/reducers/subscription.slice'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { centsToCurrency, formatCurrency } from '@/utils/currencyHelpers'
import { fetchCatchUpBKCoupon } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import { getProductPermissions } from '@/actions/permissionActions'
import { fetchFreeTrialLength } from '../helpers'
import {
  catchUpBkTreatment,
  discountedCatchUpBkAmount,
  getProductByInterval,
  planSubText,
} from './helpers'
import { useAnnualCommitmentPrice, useCatchupBookkeepingPrice } from './hooks'
import { CatchupBookkeepingSection } from './CatchupBookkeepingSection'
import { PlanDetailsSection } from './PlanDetailsSection'
import { TextColors, TypographyStyles } from '@/styles'
import { Card, Separator, Text, Toggle } from '@/ui'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'

export const StripeProductCard = ({
  stripeProducts,
  selectedProduct,
  onSelect,
  freeTrialEnabled,
  freeTrialSearchParamValue,
  useTwoCheckoutFlow,
}: {
  stripeProducts: StripeProduct[]
  selectedProduct?: StripeProduct
  onSelect: (val: StripeProduct) => void
  freeTrialEnabled: boolean
  freeTrialSearchParamValue: string | null
  useTwoCheckoutFlow: boolean
}) => {
  const cubkWaiverEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.waiveCubkForFreeTrial2026,
    false
  )
  const catchUpBk = catchUpBkTreatment({
    trialCode: freeTrialSearchParamValue,
    freeTrialEnabled,
    useTwoCheckoutFlow,
    waiverEnabled: cubkWaiverEnabled,
  })
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const annualPlan = getProductByInterval(stripeProducts, StripeInterval.year)
  const monthlyPlan = getProductByInterval(stripeProducts, StripeInterval.month)
  const isAnnual = selectedProduct !== monthlyPlan
  const planTier = selectedProduct?.metadata.tier
  const catchUpBKCoupon = useFetchResponse(fetchCatchUpBKCoupon, isAnnual)
  const productPermissions = useFetchResponse(getProductPermissions, {
    productIds: stripeProducts.map((p) => p.id),
  })
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const { productPriceAsCurrency } = useAnnualCommitmentPrice()
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const track = useAnalyticsTrack()

  useEffect(() => {
    if (planTier) {
      track(
        `viewed ${planTier}, ${isAnnual ? 'Annual Plan' : 'Monthly Plan'} option in choose-plan`
      )
    }
  }, [track, isAnnual, planTier])

  useEffect(() => {
    if (annualPlan) onSelect(annualPlan)
  }, [onSelect, annualPlan])

  const productPrice = productPriceAsCurrency(selectedProduct)

  const totalDueToday = useMemo(() => {
    if (catchupBookkeepingPrice) {
      return productPrice.add(
        discountedCatchUpBkAmount(
          catchupBookkeepingPrice.amount,
          catchUpBKCoupon?.percent_off
        )
      )
    }

    return productPrice
  }, [catchupBookkeepingPrice, productPrice, catchUpBKCoupon])

  const subText = useMemo(() => {
    const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)

    if (freeTrialEnabled) {
      const discountedCubk = catchupBookkeepingPrice
        ? discountedCatchUpBkAmount(
            catchupBookkeepingPrice.amount,
            catchUpBKCoupon?.percent_off
          )
        : null
      return planSubText({
        treatment: catchUpBk,
        freeTrialDays,
        productPriceLabel: formatCurrency(productPrice, {
          hideDecimalsIfZero: true,
        }),
        isAnnual,
        catchUpBkLabel:
          discountedCubk && discountedCubk.value > 0
            ? formatCurrency(discountedCubk, { hideDecimalsIfZero: true })
            : null,
      })
    } else if (catchupBookkeepingPrice) {
      return `Then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }`
    }

    return `Billed ${isAnnual ? 'annually' : 'monthly'}`
  }, [
    freeTrialEnabled,
    freeTrialSearchParamValue,
    catchupBookkeepingPrice,
    catchUpBKCoupon,
    isAnnual,
    productPrice,
    catchUpBk,
  ])

  const handlePlanSelection = () => {
    if (isAnnual && monthlyPlan) {
      onSelect(monthlyPlan)
    } else if (!isAnnual && annualPlan) {
      onSelect(annualPlan)
    }
  }

  const monthlySavingsOnAnnualPlan = useMemo(() => {
    if (!isAnnual || !annualPlan) return null
    const annualMonthlyPrice = centsToCurrency(
      annualPlan.default_price.unit_amount
    ).divide(12)
    const monthlyPrice = productPriceAsCurrency(monthlyPlan)
    const monthlySavings = monthlyPrice.subtract(annualMonthlyPrice)
    return formatCurrency(monthlySavings, { hideDecimalsIfZero: true })
  }, [isAnnual, annualPlan, monthlyPlan, productPriceAsCurrency])

  const showFreeTrialAnnualVersion =
    freeTrialEnabled && isAnnual && !hasAnnualCommitment

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-row gap-2">
        <Toggle
          size="lg"
          variant="outline"
          checked={isAnnual}
          onCheckedChange={() => handlePlanSelection()}
        >
          <Text className="font-medium">
            {hasAnnualCommitment ? 'Annual Payment' : 'Annual Plan'}
          </Text>
          {!hasAnnualCommitment && (
            <Text className="text-green-50">Best Value</Text>
          )}
        </Toggle>
        <Toggle
          size="lg"
          variant="outline"
          checked={!isAnnual}
          onCheckedChange={() => handlePlanSelection()}
        >
          <Text className="font-medium">
            {hasAnnualCommitment ? 'Monthly Payment' : 'Monthly Plan'}
          </Text>
        </Toggle>
      </div>

      <Card className={`!mt-0 ${isMobile ? 'px-4' : ''}`}>
        <PlanDetailsSection
          stripeProducts={stripeProducts}
          selectedProduct={selectedProduct}
          freeTrialEnabled={freeTrialEnabled}
          freeTrialSearchParamValue={freeTrialSearchParamValue}
          monthlySavingsOnAnnualPlan={monthlySavingsOnAnnualPlan}
          productPermissions={productPermissions}
        />

        <CatchupBookkeepingSection
          selectedAnnualPlan={isAnnual}
          planTier={planTier}
          freeTrialEnabled={freeTrialEnabled}
          catchUpBkTreatment={catchUpBk}
          selectedProductId={selectedProduct?.id}
          productPermissions={productPermissions}
        />

        <Separator className="mt-10 mb-4" />

        <div className="flex flex-col gap-4">
          <div className="flex items-center">
            <Text className="w-1/2" variant={TypographyStyles.LABEL_LG}>
              Total Due Today
            </Text>

            <div className="w-1/2 text-right">
              {showFreeTrialAnnualVersion && (
                <Text
                  variant={TypographyStyles.DISPLAY_XXS}
                  className="inline line-through mr-2"
                  color={TextColors.MUTED}
                >
                  {formatCurrency(totalDueToday, {
                    hideDecimalsIfZero: true,
                  })}
                </Text>
              )}

              <Text
                variant={
                  isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_MD
                }
                className="inline"
              >
                {formatCurrency(
                  freeTrialEnabled ? currency(0) : totalDueToday,
                  {
                    hideDecimalsIfZero: true,
                  }
                )}
              </Text>

              {!catchupBookkeepingPrice && (
                <Text
                  variant={isMobile ? TypographyStyles.H2 : TypographyStyles.H1}
                  className="inline"
                >
                  /{isAnnual ? 'yr' : 'mo'}
                </Text>
              )}
            </div>
          </div>

          <Text
            className="min-h-fit text-right"
            color={TextColors.BRAND}
            variant={TypographyStyles.BODY_MD}
          >
            {subText}
          </Text>
        </div>
      </Card>
    </div>
  )
}
